import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { API } from '../api';
import { download } from '../utils';
import { useQueryContext } from '../utils/QueryContext';
import SidePanel from '../ui/SidePanel';
import Button from '../ui/Button';
import { RadioRows, Segmented } from '../ui/Field';
import { useToast } from '../ui/Toast';

const SCOPE_OPTIONS = [
  { value: 'list', label: 'This list', description: 'Every row that matches the current filters, not only this page.' },
  { value: 'dataset', label: 'A whole dataset', description: 'All rows of one dataset, narrowed only by environment.' },
];
const DATASET_OPTIONS = [
  { value: 'vulns', label: 'Vulnerabilities' },
  { value: 'hosts', label: 'Hosts' },
  { value: 'versions', label: 'Package versions' },
  { value: 'image_findings', label: 'Container image findings' },
];
// Datasets with no environment; container images are not split by region.
const NO_ENV_DATASETS = ['image_findings'];
const ENV_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'bsg', label: 'BSG' },
  { value: 'enterprise', label: 'Enterprise' },
];
const FORMAT_OPTIONS = [
  { value: 'json', label: 'JSON' },
  { value: 'csv', label: 'CSV' },
];

// Endpoint names recorded by withPagination → the request that re-runs them, and the file name.
const LIST_EXPORTS = {
  packages: [API.GET_PACKAGES, 'packages'],
  hosts: [API.GET_HOSTS, 'hosts'],
  vulnerabilities: [API.GET_VULNS, 'vulnerabilities'],
  vulns_for_host: [API.GET_VULNS_FOR_HOST, 'host_vulnerabilities'],
  hosts_for_package: [API.GET_HOSTS_FOR_PACKAGE, 'hosts_for_package'],
  versions_for_package: [API.GET_VERSIONS_FOR_PACKAGE, 'versions_for_package'],
  vulns_for_package: [API.GET_VULNS_FOR_PACKAGE, 'vulns_for_package'],
  packages_for_host: [API.GET_PACKAGES_FOR_HOST, 'packages_for_host'],
  vuln_findings: [API.GET_VULN_FINDINGS, 'hosts_for_vuln'],
  affected_hosts_for_cve: [API.GET_AFFECTED_HOSTS_FOR_CVE, 'hosts_for_vuln'],
  images: [API.GET_IMAGES, 'container_images'],
};
// Only these endpoints honor export=csv; the others always answer JSON.
const CSV_ENDPOINTS = ['packages', 'hosts', 'vulnerabilities', 'images'];
// The API exports image findings only with a severity list; all five is every finding.
const ALL_IMAGE_SEVERITIES = 'Critical,High,Medium,Low,Unknown';
const DATASET_EXPORTS = {
  vulns: [API.GET_VULNS, 'vulns_for_packages'],
  hosts: [API.GET_HOSTS, 'hosts_for_packages'],
  versions: [API.GET_PACKAGES, 'versions_for_packages'],
  image_findings: [(params) => API.GET_ALL_IMAGE_FINDINGS({ ...params, severity: ALL_IMAGE_SEVERITIES }), 'container_image_findings'],
};

// The API refuses vulnerability exports without one of these filters.
const VULN_EXPORT_FILTERS = ['severity', 'package_name', 'fixed'];
const VULN_FILTER_HINT = 'The server exports vulnerabilities only with a severity, package or fix filter, so it will refuse this one.';

// The same requests the legacy export dialog sent.
async function runExport({ scope, dataset, env, format, lastQuery }) {
  if (scope === 'list') {
    const match = LIST_EXPORTS[lastQuery.endpoint];
    if (!match) throw new Error(`This list can't be exported (${lastQuery.endpoint}).`);
    const [request, name] = match;
    return { data: await request({ ...lastQuery.params, export: format, offset: 0, limit: -1 }), fileName: `${name}.${format}` };
  }
  const [request, name] = DATASET_EXPORTS[dataset];
  const region = env !== 'all' && !NO_ENV_DATASETS.includes(dataset) ? env : undefined;
  const data = await request({ region, export: format, offset: 0, limit: -1 });
  return { data, fileName: `${name}.${format}` };
}

function ExportPanel({ open, onClose, env: listEnv, query }) {
  const { lastQuery: trackedQuery } = useQueryContext();
  const lastQuery = query || trackedQuery;
  const toast = useToast();
  const [form, setForm] = useState({ scope: 'list', dataset: 'vulns', env: 'all', format: 'json' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!open) return;
    setForm((prev) => ({ ...prev, scope: lastQuery ? 'list' : 'dataset', env: listEnv || 'all' }));
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const scopeOptions = SCOPE_OPTIONS.map((o) => (o.value === 'list' && !lastQuery ? { ...o, description: 'Available once the list has loaded.' } : o));
  const listMissesVulnFilter = lastQuery?.endpoint === 'vulnerabilities' && !VULN_EXPORT_FILTERS.some((k) => lastQuery.params?.[k]);
  const hint = form.scope === 'list'
    ? listMissesVulnFilter && VULN_FILTER_HINT
    : form.dataset === 'vulns' && VULN_FILTER_HINT;
  const jsonOnly = form.scope === 'list' && lastQuery && !CSV_ENDPOINTS.includes(lastQuery.endpoint);
  const format = jsonOnly ? 'json' : form.format;

  const submit = async (event) => {
    event.preventDefault();
    if (form.scope === 'list' && !lastQuery) return;
    setBusy(true);
    setError(null);
    try {
      const { data, fileName } = await runExport({ ...form, format, lastQuery });
      download(data, format === 'csv' ? 'text/csv' : 'application/json', fileName);
      toast.success(`Downloaded ${fileName}`);
      onClose();
    } catch (e) {
      setError(e.message || 'The request did not complete.');
    } finally {
      setBusy(false);
    }
  };

  const formatLabel = format.toUpperCase();

  return (
    <SidePanel
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Export"
      description="Download rows as a JSON or CSV file."
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" form="export-form" variant="primary" disabled={busy || (form.scope === 'list' && !lastQuery)}>
            {busy ? 'Preparing file…' : `Download ${formatLabel}`}
          </Button>
        </>
      )}
    >
      <form id="export-form" onSubmit={submit} noValidate className="space-y-5">
        <RadioRows name="export-scope" label="What to export" options={scopeOptions} value={form.scope} onChange={set('scope')} disabled={busy} />

        {form.scope === 'dataset' && (
          <>
            <RadioRows name="export-dataset" label="Dataset" options={DATASET_OPTIONS} value={form.dataset} onChange={set('dataset')} disabled={busy} />
            {!NO_ENV_DATASETS.includes(form.dataset) && (
              <div>
                <p className="mb-1 text-small font-semibold text-ink" aria-hidden="true">Environment</p>
                <Segmented label="Environment" options={ENV_OPTIONS} value={form.env} onChange={set('env')} disabled={busy} />
              </div>
            )}
          </>
        )}

        <div>
          <p className="mb-1 text-small font-semibold text-ink" aria-hidden="true">Format</p>
          <Segmented label="Format" options={jsonOnly ? FORMAT_OPTIONS.slice(0, 1) : FORMAT_OPTIONS} value={format} onChange={set('format')} disabled={busy} />
          {jsonOnly && <p className="mt-1 text-small text-graphite">The server exports this list only as JSON.</p>}
        </div>

        {hint && <p className="text-small text-graphite">{hint}</p>}

        {error && (
          <div role="alert" className="px-3 py-2.5 rounded-control border border-crit/40 bg-crit-tint">
            <p className="text-body font-semibold text-crit">Export failed</p>
            <p className="mt-1 font-mono text-small text-ink break-words">{error}</p>
          </div>
        )}
      </form>
    </SidePanel>
  );
}

/**
 * "Export" button for list pages and entity page tabs. env: the list's environment filter,
 * preselected for dataset exports. query: { endpoint, params } of the table to export as
 * "This list"; without it the last tracked list query is used.
 */
export default function ExportButton({ env, query }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)} aria-haspopup="dialog">
        <Download size={15} strokeWidth={1.75} className="text-graphite" aria-hidden="true" />
        Export
      </Button>
      <ExportPanel open={open} onClose={() => setOpen(false)} env={env} query={query} />
    </>
  );
}
